import { App, useCurrentApp, useCurrentLocale } from '@finn/ui-utils';
import { useSearchParams } from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';

import {
  createProductFetchers,
  GenericVehicleDetails,
  QueryFilterMapper,
  SortKey,
  useProductListItems,
} from '../../../core';

type UseProductGroup = {
  initialProduct: GenericVehicleDetails;
  initialGroupedProducts?: GenericVehicleDetails[];
};
export const useGroupedProducts = ({
  initialProduct,
  initialGroupedProducts = [],
}: UseProductGroup) => {
  const { locale } = useCurrentLocale();
  const { fetchProduct } = createProductFetchers();
  const [selectedGroupColor, setSelectedGroupColor] = useState<
    Record<string, GenericVehicleDetails>
  >({});
  const [isLoadingProduct, setIsLoadingProduct] = useState<
    Record<string, boolean>
  >({});

  const updateColorOption = useCallback(
    async (rowNumber: number, newConfigId: number) => {
      setIsLoadingProduct((prev) => ({
        ...prev,
        [rowNumber]: true,
      }));
      const newProduct = await fetchProduct({
        vehicleId: newConfigId.toString(),
        locale,
      });

      setSelectedGroupColor((prev) => ({
        ...prev,
        [rowNumber]: newProduct,
      }));
      setIsLoadingProduct((prev) => ({
        ...prev,
        [rowNumber]: false,
      }));

      return newProduct;
    },
    [locale, selectedGroupColor]
  );

  const searchParams = useSearchParams();
  const isB2BApp = useCurrentApp() === App.B2B;
  const pdpPage = isB2BApp ? 'product' : 'pdp';

  const queryMapper = new QueryFilterMapper(`/${pdpPage}/${initialProduct.id}`);

  const filterValues = queryMapper.deserialize(`?${searchParams.toString()}`);

  const { products, isLoading } = useProductListItems(
    {
      filters: {
        ...filterValues,
        brands: [initialProduct.brand.id],
        models: [initialProduct.model],
        sort: SortKey.ASC,
      },
      limit: 50,
      // groupBy: ProductGroupBy.MODEL_VERSION, // TODO: re-enable this after solving allocation issues
    },
    {
      fallbackData: {
        results: initialGroupedProducts,
        offset: 0,
        total_results: initialGroupedProducts.length,
      },
      revalidateOnMount: true,
    }
  );

  const productsWithSelectedColor = useMemo(() => {
    return products.map((product, index) => {
      if (selectedGroupColor[index]) {
        return selectedGroupColor[index];
      }

      return product;
    });
  }, [products, selectedGroupColor]);

  return {
    products: productsWithSelectedColor,
    updateColorOption,
    isLoadingProduct,
    isLoading,
  };
};
